import { useState, useCallback, useRef, useEffect } from "react";

import { useTranslation } from "react-i18next";
export const useHttpClient = () => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState();

  const activeHttpRequests = useRef([]);

  const sendRequest = useCallback(
    async (url, method = "GET", body = null, headers = {}, isApplicationJson = true, isResponseBlob = false) => {
      let sentHeaders = { ...headers };
      if (!headers?.Authorization) {
        const userData = JSON.parse(localStorage.getItem("clientData"));
        if (userData?.token) {
          if (isApplicationJson) {
            sentHeaders = {
              Authorization: "Bearer " + userData.token,
              "Content-Type": "application/json",
              ...sentHeaders,
            };
          } else {
            sentHeaders = { Authorization: "Bearer " + userData.token, ...sentHeaders };
          }
        }
      }
      setIsLoading(true);
      const httpAbortCtrl = new AbortController();
      activeHttpRequests.current.push(httpAbortCtrl);

      try {
        const response = await fetch(url, {
          method,
          body,
          headers: sentHeaders,
          signal: httpAbortCtrl.signal,
        });

        let responseData;
        if (!isResponseBlob) {
          responseData = await response.json();
        } else if (response.ok) {
          responseData = await response.blob();
        } else {
          // Blob responses (file downloads) still return JSON on 4xx/5xx.
          // Blob has no .message — Error(undefined) shows up as "Uncaught Error".
          const text = await response.text();
          try {
            responseData = text ? JSON.parse(text) : {};
          } catch {
            responseData = {};
          }
        }

        activeHttpRequests.current = activeHttpRequests.current.filter((reqCtrl) => reqCtrl !== httpAbortCtrl);
        if (!response.ok) {
          const error = new Error(responseData?.message || `Request failed (${response.status})`);
          error.code = response.status;
          if (error.code === 628) {
            window.toast.error(t("httpCodes." + error.code));
          }
          throw error;
        }

        setIsLoading(false);
        return responseData;
      } catch (err) {
        setError(err.message);
        setIsLoading(false);
        throw err;
      }
    },
    []
  );

  const clearError = () => {
    setError(null);
  };

  useEffect(() => {
    return () => {
      activeHttpRequests.current.forEach((abortCtrl) => abortCtrl.abort());
    };
  }, []);

  return { isLoading, error, sendRequest, clearError };
};
